import type { ReactNode } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';
import { UMBadge, UMIcon } from '../../urbanmap/_components';
import { Pill } from './ui';
import { PERIOD_LABEL, USAGE_LABEL, buildingConsumption, calculationPeriod, fmt } from './domain';
import { UMR, caption } from './tokens';
import type { DjuSettings, PlanBuilding, RatioTable } from './types';

interface Props {
  building: PlanBuilding;
  ratios: RatioTable;
  dju: DjuSettings;
  changedInUM: boolean;
  preview: boolean;
  onClose: () => void;
}

const box = { border: `1px solid ${UMR.border}`, borderRadius: 8, padding: 8 } as const;

function Cell({ title, children, span = 1, unit }: { title: string; children: ReactNode; span?: 1 | 2; unit?: string }) {
  return (
    <div style={{ ...box, gridColumn: `span ${span}` }}>
      <div style={{ ...caption, marginBottom: 4 }}>{title}</div>
      <div style={{ fontWeight: 700, lineHeight: '20px', wordBreak: 'break-word' }}>
        {children} {unit && <span style={{ ...caption, fontWeight: 400 }}>{unit}</span>}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <div style={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', paddingBottom: 4, borderBottom: `1px solid ${UMR.border}` }}>{children}</div>;
}

/** Fiche bâtiment du projet (gabarit de la fiche UM : 300 px, badge ID, cartes). */
export function BuildingCard({ building: b, ratios, dju, changedInUM, preview, onClose }: Props) {
  const c = buildingConsumption(b, ratios, dju);
  const period = calculationPeriod(b, ratios);
  return (
    <aside
      data-comment-view={`building-${b.id}`}
      data-comment-anchor={`building-${b.id}`}
      aria-label={`Bâtiment ${b.id}`}
      style={{ width: 300, minWidth: 300, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', background: UMR.white, borderLeft: `1px solid ${UMR.border}`, fontFamily: UMR.font, fontSize: 14, lineHeight: '21px', color: UMR.black }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, padding: 16, height: 64, boxSizing: 'border-box' }}>
        <UMBadge variant="information" icon="flag">{b.id}</UMBadge>
        <button type="button" title="Fermer" aria-label="Fermer la fiche" onClick={onClose} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}>
          <UMIcon name="close" family="icons" size={21} color={UMR.textSecondary} />
        </button>
      </header>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ color: UMR.textSecondary, fontSize: 14, lineHeight: '20px' }} title={b.address}>{b.address}</div>
        {changedInUM && (
          <div><Pill tone="warning" icon="update" title="Ce bâtiment a été modifié ou supprimé dans UrbanMap depuis la copie">Modifié dans UrbanMap</Pill></div>
        )}

        <SectionTitle>Caractéristiques</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Cell title="Usage" span={2}>
            {b.usage === null ? 'Catégorie Plan non déterminée' : USAGE_LABEL[b.usage]}
            <NJText scale="sm">Source : {b.sourceUsage ?? b.usageLabel ?? 'usage non fourni'}</NJText>
          </Cell>
          <Cell title="Période de construction">
            {period === null ? 'Non déterminée' : PERIOD_LABEL[period]}
            <NJText scale="sm">Année : {b.year ?? 'non fournie'}</NJText>
          </Cell>
          <Cell title="Surface chauffée" unit="m²">{fmt(b.heatedArea)}</Cell>
        </div>

        <SectionTitle>Consommation simulée</SectionTitle>
        {c.corrected === null && <NJText scale="sm">Calcul indisponible : usage Plan non classé, année, surface chauffée ou ratios de cette typologie manquants.</NJText>}
        {preview && <div style={caption}>Aperçu avec les paramètres non enregistrés.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Cell title="Ratio appliqué" unit="kWh/m²/an">{fmt(c.ratio)}</Cell>
          <Cell title="Conso. brute" unit="MWh/an">{fmt(c.raw, 1)}</Cell>
          <Cell title="Coeff. DJU">× {fmt(c.coefficient, 3)}</Cell>
          <Cell title="Conso. corrigée" unit="MWh/an">{fmt(c.corrected, 1)}</Cell>
        </div>
        <div style={{ ...caption, borderRadius: 8, background: UMR.borderSubtle, padding: '8px' }}>
          Consommation = ratio déjà calibré × surface chauffée. DJU : chauffage ; DJClim : froid ; ECS inchangée. Aucun second facteur climatique. Valeurs de démonstration.
        </div>
      </div>
    </aside>
  );
}
